
import React from 'react';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Farm } from '@/types/database.types';

interface FarmUploadStatusProps {
  isUploading: boolean;
  progress: number;
  uploadState: 'idle' | 'parsing' | 'uploading' | 'success' | 'error';
  errors: string[];
  parsedData: Partial<Farm>[];
}

const FarmUploadStatus: React.FC<FarmUploadStatusProps> = ({
  isUploading,
  progress,
  uploadState,
  errors,
  parsedData,
}) => {
  if (uploadState === 'idle' && !isUploading && errors.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {isUploading && (
        <Progress value={progress} className="h-2 w-full" />
      )}

      {uploadState === 'success' && (
        <Alert variant="success">
          <CheckCircle className="h-4 w-4" />
          <AlertTitle>Upload Complete</AlertTitle>
          <AlertDescription>
            Successfully uploaded {parsedData.length} farms
          </AlertDescription>
        </Alert>
      )}

      {uploadState === 'error' && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Upload Failed</AlertTitle>
          <AlertDescription>
            There was an error uploading the farms
          </AlertDescription>
        </Alert>
      )}

      {errors.length > 0 && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Warnings</AlertTitle>
          <AlertDescription>
            <div className="mt-2 max-h-40 overflow-y-auto text-sm">
              <ul className="list-disc pl-5 space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default FarmUploadStatus;
